import { useEffect, useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import { films, genreFilters, type Film } from "@/mocks/films";

function FilmModal({ film, onClose }: { film: Film; onClose: () => void }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={film.title}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative z-10 grid max-h-[90vh] w-full max-w-3xl overflow-auto rounded-2xl bg-background-50 md:grid-cols-[minmax(0,300px)_1fr]">
        <div className="relative aspect-[3/4] w-full overflow-hidden rounded-t-2xl md:aspect-auto md:rounded-l-2xl md:rounded-tr-none">
          <img src={film.poster} alt={film.title + " 海报"} className="h-full w-full object-cover object-top" />
        </div>
        <div className="flex flex-col p-6 md:p-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="text-xs text-primary-600">{film.original}</span>
              <h3 className="mt-1 font-heading text-2xl font-black text-foreground-950">{film.title}</h3>
              <p className="mt-1 text-xs text-foreground-500">
                {film.year} · {film.country} · {film.director} 导演
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="关闭"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200"
            >
              <i className="ri-close-line" aria-hidden="true"></i>
            </button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {film.genres.map((g) => (
              <span key={g} className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800">
                {g}
              </span>
            ))}
            {film.tags.map((t) => (
              <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                {t}
              </span>
            ))}
          </div>

          <p className="mt-5 text-sm leading-relaxed text-foreground-700">{film.blurb}</p>

          <div className="mt-5 flex items-center gap-6 border-t border-background-200 pt-5">
            <div>
              <span className="block text-xs text-foreground-500">站内评分</span>
              <span className="font-heading text-2xl font-black text-accent-600">{film.rating.toFixed(1)}</span>
            </div>
            <div>
              <span className="block text-xs text-foreground-500">时长</span>
              <span className="text-sm font-semibold text-foreground-900">{film.duration}</span>
            </div>
          </div>

          <a
            href="#faq"
            onClick={onClose}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary-600 px-5 py-3 text-sm font-semibold whitespace-nowrap text-white transition-colors hover:bg-primary-700"
          >
            <i className="ri-tv-line" aria-hidden="true"></i>
            了解如何正规观影
          </a>
        </div>
      </div>
    </div>
  );
}

export default function NewReleases() {
  const [active, setActive] = useState("全部");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Film | null>(null);

  useEffect(() => {
    const onGenre = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (detail) {
        setActive(detail);
        document.getElementById("new")?.scrollIntoView({ behavior: "smooth" });
      }
    };
    window.addEventListener("chigua-genre", onGenre);
    return () => window.removeEventListener("chigua-genre", onGenre);
  }, []);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return films.filter((f) => {
      const matchGenre = active === "全部" || f.primaryGenre === active || f.genres.includes(active);
      const matchQ =
        q === "" ||
        f.title.toLowerCase().includes(q) ||
        f.original.toLowerCase().includes(q) ||
        f.genres.some((g) => g.toLowerCase().includes(q)) ||
        f.director.includes(query.trim());
      return matchGenre && matchQ;
    });
  }, [active, query]);

  return (
    <section id="new" className="section-pad scroll-mt-20 bg-background-50">
      <div className="container-x">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="新片速递"
            title="近期热议高分影片"
            desc="编辑部为你筛选近期口碑在线、讨论度高的影片。切换题材或用关键词搜索，快速定位你的下一部好片。"
          />
          <div className="relative w-full lg:w-72">
            <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400" aria-hidden="true"></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索片名 / 题材 / 导演"
              aria-label="搜索影片"
              className="w-full rounded-full border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        {/* 题材筛选 */}
        <div className="mt-8 flex flex-wrap items-center gap-2" role="tablist" aria-label="题材筛选">
          {genreFilters.map((g) => (
            <button
              key={g}
              type="button"
              role="tab"
              aria-selected={active === g}
              onClick={() => setActive(g)}
              className={
                "rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors " +
                (active === g
                  ? "bg-primary-600 font-semibold text-white"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700")
              }
            >
              {g}
            </button>
          ))}
        </div>

        {list.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {list.map((f) => (
              <article
                key={f.id}
                className="group cursor-pointer"
                onClick={() => setSelected(f)}
              >
                <div className="relative overflow-hidden rounded-2xl border border-background-200 bg-background-100 transition-transform duration-300 group-hover:-translate-y-1">
                  <img
                    src={f.poster}
                    alt={f.title + " 海报"}
                    className="aspect-[3/4] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-xs font-bold text-accent-300 backdrop-blur-sm">
                    <i className="ri-star-fill text-[11px]" aria-hidden="true"></i>
                    {f.rating.toFixed(1)}
                  </span>
                  <div className="absolute inset-x-0 bottom-0 translate-y-2 p-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                    <p className="line-clamp-3 text-xs leading-relaxed text-white/90">{f.blurb}</p>
                  </div>
                </div>
                <h3 className="mt-3 truncate font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">
                  {f.title}
                </h3>
                <p className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
                  <span>{f.year}</span>
                  <i className="ri-checkbox-blank-circle-fill text-[4px]" aria-hidden="true"></i>
                  <span>{f.genres.slice(0, 2).join(" / ")}</span>
                </p>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 py-16 text-center">
            <i className="ri-film-line text-4xl text-background-400" aria-hidden="true"></i>
            <p className="mt-3 text-sm text-foreground-600">没有找到匹配的影片，换个题材或关键词试试吧。</p>
          </div>
        )}
      </div>

      {selected ? <FilmModal film={selected} onClose={() => setSelected(null)} /> : null}
    </section>
  );
}